import React, { useState, useEffect } from 'react';
import axios from 'axios';

// TypeScript interfaces
interface IEmailTemplate {
  _id?: string;
  name: string;
  type: 'portal_invitation' | 'password_reset' | 'reminder' | 'notification' | 'article_assignment' | 'test';
  subject: string;
  htmlContent: string;
  textContent?: string;
  variables: Array<{
    key: string;
    label: string;
    description?: string;
    type?: 'text' | 'email' | 'url' | 'date' | 'number' | 'boolean';
    required?: boolean;
    defaultValue?: string;
  }>;
  design?: {
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    fontFamily?: string;
    logoUrl?: string;
    footerText?: string;
    companyInfo?: {
      name?: string;
      address?: string;
      phone?: string;
      email?: string;
      website?: string;
    };
  };
  isActive: boolean;
  isDefault?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  lastModifiedBy?: string;
  version?: number;
}

interface EmailTemplateManagerProps {
  className?: string;
}

// SVG Icons
const EyeIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const PencilIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const SaveIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CheckCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SimpleEmailTemplateManager: React.FC<EmailTemplateManagerProps> = ({ className = '' }) => {
  const [templates, setTemplates] = useState<IEmailTemplate[]>([]);
  const [selectedType, setSelectedType] = useState<IEmailTemplate['type']>('portal_invitation');
  const [currentTemplate, setCurrentTemplate] = useState<IEmailTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState<IEmailTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // API Base URL
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Template-Typen mit Icons und deutschen Bezeichnungen
  const templateTypes = [
    { type: 'portal_invitation', icon: '📧', name: 'Portal-Einladung', color: 'from-blue-500 to-blue-600' },
    { type: 'password_reset', icon: '🔑', name: 'Passwort zurücksetzen', color: 'from-orange-500 to-orange-600' },
    { type: 'reminder', icon: '⏰', name: 'Erinnerung', color: 'from-yellow-500 to-yellow-600' },
    { type: 'notification', icon: '🔔', name: 'Fragebogen-Zuweisung', color: 'from-green-500 to-green-600' },
    { type: 'article_assignment', icon: '📋', name: 'Artikel-Zuweisung', color: 'from-purple-500 to-purple-600' }
  ] as const;

  // Templates vom Backend laden
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/email-templates');
      if (response.data && response.data.success) {
        const templatesData = response.data.data || [];
        setTemplates(templatesData);
        const current = templatesData.find((t: IEmailTemplate) => t.type === selectedType && t.isActive);
        setCurrentTemplate(current || null);
      } else {
        console.warn('Unexpected API response structure:', response.data);
        setTemplates([]);
        setCurrentTemplate(null);
      }
    } catch (error) {
      console.error('Error loading email templates:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Templates' });
      setTemplates([]);
      setCurrentTemplate(null);
    } finally {
      setLoading(false);
    }
  };

  // Template speichern
  const saveTemplate = async () => {
    if (!editedTemplate) return;

    try {
      setSaving(true);
      setMessage(null);
      
      const url = editedTemplate._id 
        ? `/api/email-templates/${editedTemplate._id}`
        : '/api/email-templates';
      
      const response = editedTemplate._id
        ? await axios.put(url, editedTemplate)
        : await axios.post(url, editedTemplate);

      if (response.data && response.data.success) {
        setMessage({ type: 'success', text: 'Template erfolgreich gespeichert!' });
        await loadTemplates();
        setIsEditing(false);
        setEditedTemplate(null);
        
        setTimeout(() => setMessage(null), 3000);
      } else {
        const errorMessage = response.data?.message || 'Unbekannter Fehler beim Speichern';
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (error: any) {
      console.error('Error saving template:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Fehler beim Speichern des Templates';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  // Template-Typ wechseln
  const handleTypeChange = (type: IEmailTemplate['type']) => {
    setSelectedType(type);
    setIsEditing(false);
    setEditedTemplate(null);
    const template = templates.find(t => t.type === type && t.isActive);
    setCurrentTemplate(template || null);
  };

  // Bearbeitung starten
  const startEditing = () => {
    if (currentTemplate) {
      setEditedTemplate({ ...currentTemplate });
    } else {
      const templateTypeNames: Record<IEmailTemplate['type'], string> = {
        portal_invitation: 'Portal-Einladung Standard',
        password_reset: 'Passwort-Reset Standard',
        reminder: 'Erinnerung Standard',
        notification: 'Fragebogen-Zuweisung Standard',
        article_assignment: 'Artikel-Zuweisung Standard',
        test: 'Test-E-Mail Standard'
      };

      const newTemplate: IEmailTemplate = {
        name: templateTypeNames[selectedType],
        type: selectedType,
        subject: 'WEDL - Neue E-Mail',
        htmlContent: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff;">
            <div style="background: #dc2626; color: white; padding: 30px; text-align: center;">
              <h1 style="margin: 0; font-size: 24px;">WEDL Handelshaus</h1>
            </div>
            <div style="padding: 30px;">
              <p style="margin-bottom: 20px; font-size: 16px; line-height: 1.5;">Hallo <strong>{{name}}</strong>,</p>
              <p style="margin-bottom: 20px; font-size: 16px; line-height: 1.5;">Dies ist eine neue E-Mail-Vorlage.</p>
              <p style="margin-bottom: 20px; font-size: 16px; line-height: 1.5;">Mit freundlichen Grüßen<br>Ihr WEDL Team</p>
            </div>
            <div style="background: #f8f9fa; padding: 20px; text-align: center; font-size: 12px; color: #666;">
              <p style="margin: 0;">WEDL Handels GmbH | Musterstraße 123 | 12345 Musterstadt</p>
            </div>
          </div>
        `,
        variables: [
          { key: 'name', label: 'Name des Empfängers', description: 'Name des Empfängers', type: 'text', required: true }
        ],
        design: {
          primaryColor: '#dc2626',
          secondaryColor: '#2c3e50',
          accentColor: '#3498db',
          fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
          footerText: 'Diese E-Mail wurde automatisch generiert. Bitte antworten Sie nicht direkt auf diese E-Mail.',
          companyInfo: {
            name: 'WEDL Handels GmbH',
            email: 'edvhotline@wedl.com',
            website: 'https://wedl.com'
          }
        },
        isActive: true,
        isDefault: false,
        createdBy: 'Admin',
        lastModifiedBy: 'Admin',
        version: 1
      };
      setEditedTemplate(newTemplate);
    }
    setIsEditing(true);
  };

  // Bearbeitung abbrechen
  const cancelEditing = () => {
    setIsEditing(false);
    setEditedTemplate(null);
  };

  // HTML-Inhalt für Preview verarbeiten
  const getPreviewHtml = (htmlContent: string, variables: IEmailTemplate['variables']) => {
    let previewHtml = htmlContent;
    
    console.log('🔍 Preview Debug:');
    console.log('  Template variables:', variables.map(v => v.key));
    console.log('  HTML Content length:', htmlContent.length);
    console.log('  HTML Content preview:', htmlContent.substring(0, 100) + '...');
    
    // Dynamische Beispielwerte basierend auf den tatsächlichen Variablen
    const exampleValues: Record<string, string> = {
      // Portal-Einladung Variablen (aus DB)
      '{{supplier_name}}': 'Musterfirma GmbH',
      '{{supplier_email}}': 'musterfirma@example.com',
      '{{inviter_name}}': 'Anna Schmidt (WEDL)',
      '{{access_token}}': 'ABC123-XYZ789-TOKEN456',
      '{{expires_at}}': '31.12.2024 23:59 Uhr',
      '{{portal_status}}': 'Aktiv',
      '{{portal_url}}': 'https://portal.wedl.de/login',
      
      // Passwort-Reset Variablen
      '{{username}}': 'musterfirma@example.com',
      '{{password}}': 'TempPass2024!',
      '{{login_url}}': 'https://portal.wedl.de/login',
      
      // Erinnerung & Fragebogen-Zuweisung Variablen
      '{{questionnaire_name}}': 'Nachhaltigkeit Fragebogen 2025',
      '{{deadline}}': '15. September 2025',
      '{{days_remaining}}': '5',
      '{{progress_percentage}}': '67',
      '{{is_overdue}}': 'false',
      '{{is_urgent}}': 'true',
      '{{status_text}}': 'Erinnerung',
      '{{assigned_by}}': 'WEDL System',
      '{{has_deadline}}': 'true',
      '{{current_year}}': '2025',
      
      // Artikel-Zuweisung Variablen
      '{{article_name}}': 'Beispiel Süßware 250g',
      '{{article_number}}': '12345-SW-250',
      '{{total_articles}}': '3',
      '{{comment}}': 'Bitte alle LMIV-Daten vollständig ausfüllen.',
      '{{is_multiple_articles}}': 'false',
      
      // Test Variablen
      '{{recipient_email}}': 'test@wedl.com',
      '{{test_type}}': 'Template Preview Test',
      '{{timestamp}}': new Date().toLocaleString('de-DE'),
      '{{current_datetime}}': new Date().toLocaleString('de-DE'),
      '{{server_info}}': 'WEDL Portal Development'
    };

    // Erst die tatsächlichen Variablen aus dem Template verwenden
    console.log('🔄 Processing template variables:', variables.length);
    variables.forEach(variable => {
      const placeholder = `{{${variable.key}}}`;
      let value = exampleValues[placeholder];
      
      console.log(`  Variable: ${placeholder} -> ${value || 'FALLBACK'}`);
      
      // Fallback basierend auf Variable-Typ
      if (!value) {
        switch (variable.type) {
          case 'email':
            value = 'beispiel@email.com';
            break;
          case 'url':
            value = 'https://beispiel.wedl.de';
            break;
          case 'date':
            value = new Date().toLocaleDateString('de-DE');
            break;
          case 'number':
            value = '123';
            break;
          case 'boolean':
            value = 'Ja';
            break;
          default:
            value = `[${variable.label || variable.key}]`;
        }
        console.log(`    Fallback used: ${value}`);
      }
      
      // Variable im HTML ersetzen - globale Ersetzung mit split/join
      const occurrencesBefore = (previewHtml.match(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g')) || []).length;
      previewHtml = previewHtml.split(placeholder).join(value);
      const occurrencesAfter = (previewHtml.match(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g')) || []).length;
      
      console.log(`    Replaced ${occurrencesBefore - occurrencesAfter} occurrences of ${placeholder} with "${value}"`);
    });

    console.log('✅ Finished processing variables. HTML length:', previewHtml.length);

    // Zusätzlich alle noch nicht ersetzten Standard-Variablen ersetzen (Fallback)
    Object.keys(exampleValues).forEach(placeholder => {
      const value = exampleValues[placeholder];
      if (previewHtml.includes(placeholder)) {
        console.log(`🔧 Fallback replacement for ${placeholder}`);
        previewHtml = previewHtml.split(placeholder).join(value);
      }
    });

    const styleSheet = `
      <style>
        .email-container { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff; }
        .email-header { background: linear-gradient(135deg, #e23139 0%, #c21807 100%); color: white; padding: 30px; text-align: center; }
        .email-title { margin: 0 0 10px 0; font-size: 24px; font-weight: bold; }
        .email-content { padding: 30px; font-size: 16px; line-height: 1.6; color: #333; }
        .info-box { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 20px; margin: 20px 0; }
        .info-box.success { background: #d4edda; border-color: #c3e6cb; }
        .info-box h3 { color: #155724; margin-top: 0; }
        .credential-item { background: rgba(255,255,255,0.9); padding: 15px; border-radius: 6px; margin: 10px 0; }
        .credential-label { font-size: 12px; color: #666; text-transform: uppercase; margin-bottom: 5px; }
        .credential-value { font-family: monospace; font-size: 16px; font-weight: bold; color: #333; word-break: break-all; }
        .btn { display: inline-block; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold; text-align: center; transition: all 0.3s ease; }
        .btn.accent { background: #3498db; color: white; }
        .btn.accent:hover { background: #2980b9; }
        .email-footer { background: #f8f9fa; padding: 20px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #e9ecef; }
        ul { padding-left: 20px; }
        li { margin-bottom: 8px; }
      </style>
    `;

    return styleSheet + previewHtml;
  };

  // Initial Templates laden
  useEffect(() => {
    loadTemplates();
  }, []);

  // Template bei Type-Änderung aktualisieren
  useEffect(() => {
    const template = templates.find(t => t.type === selectedType && t.isActive);
    setCurrentTemplate(template || null);
  }, [templates, selectedType]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <span className="mt-4 text-gray-600">Templates werden geladen...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg mb-4 border flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border-green-200' 
            : 'bg-red-50 text-red-800 border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircleIcon className="h-4 w-4 mr-2" />
          ) : (
            <XIcon className="h-4 w-4 mr-2" />
          )}
          {message.text}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Preview Column */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <EyeIcon className="h-5 w-5 text-gray-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">E-Mail Vorschau</h3>
              </div>
              <button
                onClick={isEditing ? cancelEditing : startEditing}
                className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  isEditing
                    ? 'bg-gray-600 text-white hover:bg-gray-700 hover:shadow-md'
                    : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md'
                }`}
                disabled={saving}
              >
                {isEditing ? (
                  <>
                    <XIcon className="h-3 w-3 mr-1" />
                    Abbrechen
                  </>
                ) : (
                  <>
                    <PencilIcon className="h-3 w-3 mr-1" />
                    Bearbeiten
                  </>
                )}
              </button>
            </div>

            {/* Email Preview */}
            <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              {/* Email Header */}
              <div className="bg-white p-3 border-b border-gray-200">
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="flex">
                    <span className="font-medium w-12">Von:</span>
                    <span>noreply@wedl-handelshaus.com</span>
                  </div>
                  <div className="flex">
                    <span className="font-medium w-12">Betreff:</span>
                    <span className="font-medium text-gray-900">
                      {(isEditing ? editedTemplate?.subject : currentTemplate?.subject) || 'Kein Template gefunden'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Email Body */}
              <div className="bg-white">
                {currentTemplate || editedTemplate ? (
                  <div 
                    className="min-h-[400px]"
                    dangerouslySetInnerHTML={{
                      __html: getPreviewHtml(
                        (isEditing ? editedTemplate?.htmlContent : currentTemplate?.htmlContent) || '',
                        (isEditing ? editedTemplate?.variables : currentTemplate?.variables) || []
                      )
                    }}
                  />
                ) : (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl text-gray-400">📧</span>
                    </div>
                    <p className="text-gray-500 mb-4">Kein Template für diesen Typ gefunden.</p>
                    <button
                      onClick={startEditing}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
                    >
                      Neues Template erstellen
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit/Details Column */}
        <div className="space-y-4">
          {isEditing && editedTemplate ? (
            /* Edit Form */
            <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <PencilIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Template bearbeiten</h3>
                </div>
                <button
                  onClick={saveTemplate}
                  disabled={saving}
                  className="flex items-center px-4 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium transition-all duration-200 hover:shadow-md"
                >
                  <SaveIcon className="h-3 w-3 mr-1" />
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
              </div>

              <div className="space-y-4">
                {/* Template Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={editedTemplate.name}
                    onChange={(e) => setEditedTemplate({
                      ...editedTemplate,
                      name: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Template Name eingeben..."
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    E-Mail Betreff
                  </label>
                  <input
                    type="text"
                    value={editedTemplate.subject}
                    onChange={(e) => setEditedTemplate({
                      ...editedTemplate,
                      subject: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="E-Mail Betreff eingeben..."
                  />
                </div>

                {/* HTML Content */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    HTML Inhalt
                  </label>
                  <textarea
                    value={editedTemplate.htmlContent}
                    onChange={(e) => setEditedTemplate({
                      ...editedTemplate,
                      htmlContent: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono text-xs"
                    rows={15}
                    placeholder="HTML Inhalt der E-Mail eingeben..."
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center p-3 bg-gray-50 rounded-md">
                  <input
                    type="checkbox"
                    id="active-status"
                    checked={editedTemplate.isActive}
                    onChange={(e) => setEditedTemplate({
                      ...editedTemplate,
                      isActive: e.target.checked
                    })}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="active-status" className="text-sm font-medium text-gray-700">
                    Template ist aktiv und wird für neue E-Mails verwendet
                  </label>
                </div>
              </div>
            </div>
          ) : (
            /* Template Type Selection and Variables */
            <div className="space-y-4">
              {/* Template Type Selection Card */}
              <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Template-Typ auswählen</h3>
                
                {/* Template Type Buttons Grid */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {templateTypes.map(({ type, icon, name, color }) => (
                    <button
                      key={type}
                      onClick={() => handleTypeChange(type)}
                      className={`relative px-3 py-2 rounded-md ${
                        selectedType === type
                          ? 'bg-blue-50 shadow-md ring-2 ring-blue-200 border-2 border-blue-300'
                          : 'bg-gray-50 hover:bg-white hover:shadow-md border border-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={`w-5 h-5 bg-gradient-to-r ${color} rounded flex items-center justify-center`}>
                          <span className="text-xs text-white">{icon}</span>
                        </div>
                        <div className={`font-medium text-xs whitespace-nowrap ${
                          selectedType === type ? 'text-blue-600' : 'text-gray-700'
                        }`}>
                          {name}
                        </div>
                      </div>
                      
                      {selectedType === type && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center">
                          <CheckCircleIcon className="h-2 w-2 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {/* No Template Message */}
                {!currentTemplate && (
                  <div className="text-center py-4 bg-gray-50 rounded-md">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-lg text-gray-400">❌</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-1">Kein Template für diesen Typ</p>
                    <p className="text-xs text-gray-400">Klicken Sie auf "Bearbeiten", um ein neues zu erstellen</p>
                  </div>
                )}
              </div>

              {/* Variables */}
              {currentTemplate && (
                <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Verfügbare Variablen</h3>
                  <div className="space-y-2">
                    {currentTemplate.variables.map((variable, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                        <div>
                          <code className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">
                            {`{{${variable.key}}}`}
                          </code>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">{variable.label}</div>
                          {variable.description && (
                            <div className="text-xs text-gray-500">{variable.description}</div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* Add missing password variable if not already present */}
                    {currentTemplate.type === 'portal_invitation' && !currentTemplate.variables.find(v => v.key === 'password') && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                        <div>
                          <code className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">
                            {`{{password}}`}
                          </code>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">Passwort</div>
                          <div className="text-xs text-gray-500">Temporäres Passwort</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleEmailTemplateManager;